import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { FormHelperText, Grid2, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
// eslint-disable-next-line no-restricted-imports
import { useController } from 'react-hook-form-v7';
import { COLORS } from '../../../utils/Application_Constants';
import FilePreview from './FilePreview';

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

const useStyles = makeStyles(() => ({
    dropzone: {
        borderStyle: 'dashed',
        borderWidth: '2px',
        borderColor: COLORS.LT_JOURNEY25,
        textAlign: 'center',
        borderRadius: '8px',
        cursor: 'pointer',
        opacity: '1',
        background: COLORS.LT_JOURNEY5,
        gap: '16px',
        '&:hover': {
            background: COLORS.LT_MIDNIGHTBG2,
        },

        '&.disabled': {
            '&:hover': {
                background: 'transparent',
            },
            cursor: 'initial',
            opacity: 0.5,
        },
    },
    dot: {
        height: '50px',
        width: '50px',
        backgroundColor: COLORS.LT_JOURNEY10,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: COLORS.LT_JOURNEY,
    },
    uploadTitle: {
        '& u': {
            color: COLORS.LT_JOURNEY,
        },
        fontWeight: '600',
    },
    uploadDescription: {
        fontSize: '12px',
        marginTop: '8px',
        color: COLORS.LT_MIDNIGHT50,
    },
    centerDrop: {
        padding: 16,
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'column',
    },
    filePreviews: {
        display: 'flex',
        flexDirection: 'column',
    },
    subHeader: {
        marginTop: '4px',
    },
}));

const ExpensesUploadInput = ({ control, setError }) => {
    const classes = useStyles();
    const {
        field: { onChange, value: file },
        fieldState: { error },
    } = useController({
        control,
        name: 'file',
        shouldUnregister: true,
        rules: {
            validate: { required: (value) => value || 'File is required' },
        },
    });
    const [loadingState, setLoadingState] = useState({});
    const [rejectedFiles, setRejectedFiles] = useState([]);
    const isDisabled = !!file;
    const maxFiles = 1;

    const onError = useCallback(
        (e) => {
            console.error(error);
            setError('file', { type: 'manual', message: e });
        },
        [setError]
    );

    const handleLoading = async (inputFile) => {
        let newIsLoading = { ...loadingState };
        newIsLoading = { ...newIsLoading, [inputFile.name]: { isLoading: true, duration: 1.5 } };
        setLoadingState(newIsLoading);
        await new Promise((resolve) => {
            setTimeout(resolve, 1500);
        });
        newIsLoading = { ...newIsLoading, [inputFile.name]: { isLoading: false } };
        setLoadingState(newIsLoading);
        setLoadingState({});
    };

    const onDrop = useCallback(
        async (acceptedFiles, fileRejections) => {
            if (fileRejections.length > 0) return setRejectedFiles(fileRejections);
            const [acceptedFile] = acceptedFiles;
            onChange(acceptedFile, { shouldValidate: true, shouldDirty: true });
            await handleLoading(acceptedFile);
        },
        [onChange]
    );

    const { getRootProps, getInputProps } = useDropzone({
        onError,
        onDrop,
        disabled: isDisabled,
        maxFiles,
        validator: (inputFile) => {
            if (inputFile.size > MAX_FILE_SIZE) {
                return {
                    code: 'file-too-large',
                    message: `This file must be ${MAX_FILE_SIZE / 1024 / 1024}MB or less`,
                };
            }
            if (inputFile.type !== 'text/csv') {
                return {
                    code: 'file-invalid-type',
                    message: `Invalid file type. Please upload a .csv file.`,
                };
            }
            return null;
        },
    });

    const removeFile = () => {
        onChange(null, { shouldDirty: true });
        setLoadingState({});
    };

    const removeRejectedFile = (fileIndex) => {
        setRejectedFiles(rejectedFiles.filter((_, index) => index !== fileIndex));
    };

    return (
        <>
            <Grid2
                {...getRootProps({
                    className: `${classes.dropzone} ${isDisabled && 'disabled'}`,
                })}
            >
                <Grid2 className={classes.centerDrop}>
                    <Grid2 className={classes.dot}>
                        <FileUploadOutlinedIcon className={classes.icon} />
                    </Grid2>
                    <Typography variant="body2" className={classes.uploadTitle}>
                        <u>Click to upload</u> or Drag and Drop
                    </Typography>
                    <Typography variant="body2" className={classes.uploadDescription}>
                        Max file size: 10MB | .csv
                    </Typography>
                    <input name="file" {...getInputProps()} type="file" disabled={isDisabled} />
                </Grid2>
            </Grid2>

            <Grid2 size={12} className={classes.subHeader}>
                <Typography variant="caption" color="textSecondary">
                    {file || rejectedFiles.length ? 'Uploaded' : 'No documents attached'}
                </Typography>
            </Grid2>
            {file && (
                <div className={classes.filePreviews}>
                    <FilePreview
                        fileIndex={0}
                        file={file}
                        loadingState={loadingState}
                        removeFile={removeFile}
                        mode="credit-card-file-upload"
                        variant={loadingState[file.name]?.isLoading ? undefined : 'success'}
                        message={
                            loadingState[file.name]?.isLoading
                                ? undefined
                                : 'Your file is ready for uploading'
                        }
                    />
                </div>
            )}

            {rejectedFiles?.length > 0 &&
                rejectedFiles.map(({ file: rejectedFile, errors }, i) => (
                    <FilePreview
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${rejectedFile.name}-${i}`}
                        fileIndex={i}
                        file={rejectedFile}
                        loadingState={loadingState}
                        mode="credit-card-file-upload"
                        variant="error"
                        message={errors.map((e) => e.message).join(', ')}
                        removeFile={removeRejectedFile}
                    />
                ))}

            <FormHelperText error>{error && error.message}</FormHelperText>
        </>
    );
};

export default ExpensesUploadInput;
